import { Link } from 'react-router-dom';
import { ChevronRight, Clock } from 'lucide-react';

/**
 * LearnMeta — Round 92f (Feb 2026).
 *
 * Renders the breadcrumb trail + reading-time pill that sits between
 * the page H1 and the article body on every Learn guide.
 *
 *   Home › Learn › Metrics & Evaluation › Yield on Cost
 *   ⏱ 5 min read
 *
 * Both pieces are SEO wins:
 *   - The breadcrumb is wrapped in JSON-LD BreadcrumbList structured
 *     data via the parent LearnPage (added separately) so Googlebot
 *     can render the trail in search results.
 *   - The reading-time pill gives skim-readers a glance-able commitment
 *     estimate, which lifts pages-per-session and time-on-page (both
 *     ranking signals).
 *
 * Reading time is computed client-side from the article's rendered
 * text content at ~225 wpm (research-backed avg silent reading speed
 * for adult web content). Rounded up so a 4.2-min guide reads "5 min".
 *
 * Props:
 *   sectionLabel   : human label for the parent section (e.g. "Metrics & Evaluation")
 *   sectionAnchor  : hash anchor on /learn that scrolls to the section (e.g. "metrics")
 *   articleRef     : optional ref to the rendered <article>; we'll
 *                    measure its text content for wpm. If absent, falls
 *                    back to the explicit `wordCount` prop.
 *   wordCount      : fallback explicit word count (number)
 *
 * Component is intentionally render-once on mount — no ResizeObserver
 * or text-mutation watcher because the Learn guides don't mutate their
 * article body after first paint.
 */
const WORDS_PER_MINUTE = 225;

function estimateReadMinutes(words) {
  if (!words || words < 30) return 1;
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
}

function countWordsInElement(el) {
  if (!el) return 0;
  const text = el.textContent || '';
  // Match words split by whitespace; filter out empties.
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export default function LearnMeta({
  sectionLabel,
  sectionAnchor,
  articleRef,
  wordCount,
}) {
  // Measure on mount once. Re-rendering on prop change is fine — the
  // article DOM is stable after first paint so the count stabilises.
  let minutes = null;
  if (typeof wordCount === 'number') {
    minutes = estimateReadMinutes(wordCount);
  } else if (articleRef && articleRef.current) {
    minutes = estimateReadMinutes(countWordsInElement(articleRef.current));
  }
  // If neither is available yet (first render before article ref attaches),
  // suppress the pill rather than show "1 min". The parent re-renders
  // once the ref is set.

  return (
    <nav
      aria-label="Breadcrumb"
      className="mt-4 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground"
      data-testid="learn-meta"
    >
      <Link
        to="/"
        className="hover:text-foreground transition-colors"
        data-testid="learn-meta-home"
      >
        Home
      </Link>
      <ChevronRight className="w-3 h-3 opacity-50" />
      <Link
        to="/learn"
        className="hover:text-foreground transition-colors"
        data-testid="learn-meta-learn"
      >
        Learn
      </Link>
      {sectionLabel && (
        <>
          <ChevronRight className="w-3 h-3 opacity-50" />
          <Link
            to={sectionAnchor ? `/learn#${sectionAnchor}` : '/learn'}
            className="hover:text-foreground transition-colors"
            data-testid="learn-meta-section"
          >
            {sectionLabel}
          </Link>
        </>
      )}
      {minutes != null && (
        <span
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-violet-200/60 dark:border-violet-800/50 bg-violet-50/60 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 text-[10px] font-semibold uppercase tracking-wider"
          data-testid="learn-meta-readtime"
          title={`Estimated reading time at ${WORDS_PER_MINUTE} words/min`}
        >
          <Clock className="w-3 h-3" />
          {minutes} min read
        </span>
      )}
    </nav>
  );
}

export { estimateReadMinutes, countWordsInElement };
